
import React, { useEffect, useState } from 'react';
import { 
  Popover, 
  PopoverContent, 
  PopoverTrigger 
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Bell } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { ReferralProgress } from './referral/ReferralProgress';
import { InvitationForm } from './referral/InvitationForm';
import { ReferralService } from './referral/ReferralService';

interface ReferralPopoverProps {
  userId: string;
  isMobile?: boolean;
}

export const ReferralPopover = ({ userId, isMobile = false }: ReferralPopoverProps) => {
  const [referralLink, setReferralLink] = useState<string | null>(null);
  const [invitedCount, setInvitedCount] = useState(0);
  const { toast } = useToast();
  const [showNotification, setShowNotification] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [formData, setFormData] = useState({
    inviterName: '',
    inviterEmail: '',
    emails: ['']
  });

  useEffect(() => {
    if (userId) {
      fetchReferralData();
      generateReferralLink();
    }
  }, [userId]);

  const fetchReferralData = async () => {
    const { data: invitationsCount } = await supabase
      .from('email_invitations')
      .select('id', { count: 'exact' })
      .eq('user_id', userId);

    const count = invitationsCount?.length || 0;
    setInvitedCount(count % 10);
    setShowNotification(count % 10 < 10);
  };

  const generateReferralLink = async () => {
    try {
      const { data: existingLink } = await supabase
        .from('referral_links')
        .select('code')
        .eq('user_id', userId)
        .maybeSingle();

      if (existingLink?.code) {
        setReferralLink(existingLink.code);
        return;
      }

      const { data: newLink, error } = await supabase
        .from('referral_links')
        .insert([{ user_id: userId }])
        .select()
        .single();

      if (error) {
        console.error('Error generating referral link:', error);
        return;
      }

      if (newLink) {
        setReferralLink(newLink.code);
      }
    } catch (error) {
      console.error('Error in generateReferralLink:', error);
    }
  };

  const handleSubmit = async () => {
    if (!referralLink) {
      toast({
        title: "Error",
        description: "Failed to generate referral link. Please try again.",
        variant: "destructive",
      });
      return;
    }

    if (!formData.inviterName || !formData.inviterEmail) {
      toast({
        title: "Missing information",
        description: "Please provide your name and email",
        variant: "destructive",
      });
      return;
    }

    const validEmails = formData.emails.filter(email => email.trim() !== '');
    if (validEmails.length === 0) {
      toast({
        title: "No emails provided",
        description: "Please add at least one email address",
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);

    try {
      const newInvitees = await ReferralService.checkExistingInvitations(userId, validEmails);
      const skippedEmails = validEmails.filter(email => !newInvitees.includes(email));

      if (newInvitees.length === 0) {
        toast({
          title: "Cannot send invitations",
          description: "All these emails have been invited within the last 24 hours. Please try again later.",
          variant: "destructive",
        });
        return;
      }

      const invitationData = {
        inviterName: formData.inviterName,
        inviterEmail: formData.inviterEmail,
        referralCode: referralLink,
        invitedEmails: newInvitees
      };

      await ReferralService.createInvitations(userId, invitationData);
      await ReferralService.sendInvitationEmails(invitationData);

      if (skippedEmails.length > 0) {
        toast({
          title: "Partial success",
          description: `Sent ${newInvitees.length} invitation(s). ${skippedEmails.length} email(s) were skipped as they were recently invited.`,
        });
      } else {
        toast({
          title: "Invitations sent!",
          description: `Successfully sent invitations to ${newInvitees.length} friends.`,
        });
      }

      setFormData({
        inviterName: '',
        inviterEmail: '',
        emails: ['']
      });
      
      fetchReferralData();

    } catch (error) {
      console.error('Error sending invitations:', error);
      toast({
        title: "Error sending invitations",
        description: "Please try again later",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const content = (
    <div className="space-y-4">
      <div className="text-center">
        <h3 className="font-semibold text-lg">Invite Friends</h3>
      </div>
      
      <ReferralProgress invitedCount={invitedCount} />

      <InvitationForm
        formData={formData}
        onFormDataChange={setFormData}
        onSubmit={handleSubmit}
        isProcessing={isProcessing}
      />
    </div>
  );

  if (isMobile) {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <button className="w-full text-left">
            <div className="bg-blue-50 px-3 py-1.5 rounded-md text-sm text-blue-600 font-medium animate-pulse">
              Invite friends
            </div>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-80">
          {content}
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {showNotification && (
            <span className="absolute top-0 right-0 h-3 w-3 bg-red-500 rounded-full animate-pulse" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80">
        {content}
      </PopoverContent>
    </Popover>
  );
};
